import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DOM_ELEMENT_DIMENSIONS__FLOAT_PLAN_TRIP_DEFAULT } from '../../../../configurations/constants';
import { telemetry } from '../../../../services/backend';
import {
  loadTripPlanMarker,
  removeAllTripPlanMarkers,
} from '../../../../services/map-layer/custom-layers/trip-plan';
import { loadFloatPlanTripMap } from '../../../../services/map-layer/line-layers/loaded-trips';
import { useAppDispatch } from '../../../../services/store';
import {
  hideAllLayers,
  hideControlPane,
  hideControlPaneReset,
} from '../../../../services/store/modules/app-controls';
import {
  clearActiveTripHidden,
  setActiveTripHidden,
} from '../../../../services/store/modules/log-trips';
import { selectPoints } from '../../../../services/store/modules/plan';
import { calculateMapPadding } from '../../../../services/utilities';
import { locationSelectFactory } from '../../../shared/vector-images';

const useMapPlanPoints = () => {
  const points = useSelector(selectPoints);
  const dispatch = useAppDispatch();

  // Update the map to only show certain layers
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      dispatch(setActiveTripHidden());
      dispatch(hideAllLayers(true));
      dispatch(hideControlPane());
    }
    return () => {
      isMounted = false;
      dispatch(clearActiveTripHidden());
      dispatch(hideControlPaneReset());
      dispatch(hideAllLayers(false));
      telemetry(telemetry.DEBUG, '[PlanDetailsPage.useEffect] unmounted');
    };
  }, [dispatch]);

  // Load the points into the map
  useEffect(() => {
    const promises: Promise<void>[] = [];
    Object.values(points).forEach((point) => {
      promises.push(
        loadTripPlanMarker(
          point.longitude,
          point.latitude,
          locationSelectFactory(point.ordinal + 1),
        ),
      );
    });
    return () => {
      removeAllTripPlanMarkers();
    };
  }, [points]);

  useEffect(() => {
    // get the padding for the map and move it to the correct place to show both trip markers in the middle
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { mapPadding } = calculateMapPadding(
      DOM_ELEMENT_DIMENSIONS__FLOAT_PLAN_TRIP_DEFAULT,
      true,
      0,
      true, // allowForMarkers
    );
    const { latitude: startLat, longitude: startLon } = points[0];
    const { latitude: endLat, longitude: endLon } = points[points.length - 1];

    if (startLat && startLon && endLat && endLon) {
      loadFloatPlanTripMap(
        { lat: startLat, lon: startLon },
        { lat: endLat, lon: endLon },
        points
          .filter((point) => point?.latitude && point?.longitude)
          .map((point) => ({
            latLon: {
              lat: point.latitude,
              lon: point.longitude,
            },
          })),
        mapPadding,
      );
    }
  }, [points]);
};

export default useMapPlanPoints;
